import { isEmpty } from 'ramda';

import { Howl, Howler, HowlOptions } from '@ezugi/howler';

import { volumeSelector, backgroundMusicSoundSelector } from '../../selectors';
import { retailModeSelector } from '../../selectors/app';
import { RootState } from '../../types';

export type SoundCache = {
  [key: string]: Howl | null;
  backgroundMusic: Howl | null;
};

const cache: SoundCache = {
  backgroundMusic: null,
};

const addToCache = (identifier: string, sound: Howl) => {
  cache[identifier] = sound;
};

type SourceType = string;

type BaseCacheParams = {
  spriteJson?: HowlOptions;
  sources: SourceType[]
}

type CacheWithSoundName = BaseCacheParams & { soundName: string; spriteName?: undefined };
type CacheWithSpriteName = BaseCacheParams & { spriteName: string; soundName?: undefined };

type CreateAndCacheParams = CacheWithSoundName | CacheWithSpriteName;

/**
 *
 * @param {String} soundName Sound name
 * @param {String} spriteName Manifest and sprite share the same name
 * @param {Object} spriteJson Sprite object with sound config(looping, volume, etc)
 */
const createAndCacheSound = async ({
  soundName,
  spriteName,
  spriteJson,
  sources,
}: CreateAndCacheParams): Promise<Howl> => {
  const identifier = (spriteName ?? soundName) as string;

  const sound = new Howl({
    ...spriteJson,
    src: sources,
  });

  addToCache(identifier, sound);

  return sound;
};

const createAndCacheBackgroundMusic = async (spriteJson: HowlOptions): Promise<Howl | null> => {
  if (isEmpty(spriteJson)) return null;

  const backgroundMusic = new Howl({
    loop: true,
    ...spriteJson as HowlOptions,
  });

  cache.backgroundMusic = backgroundMusic;

  return backgroundMusic;
};

const updateHowlerSettings = (state: RootState) => {
  const { value, muted } = volumeSelector(state);
  const backgroundMusic = backgroundMusicSoundSelector(state);

  Howler.volume(Number(value) / 100);
  Howler.mute(muted);
  Howler.autoUnlock = retailModeSelector(state);
  Howler.autoSuspend = false;

  // Mute/Unmute backgroundMusic separately
  cache.backgroundMusic?.mute(!backgroundMusic);
};

export {
  createAndCacheSound,
  createAndCacheBackgroundMusic,
  updateHowlerSettings,
};

export default cache;
